import { useCallback, useEffect, useState } from "react";
import { ROUTES } from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { SIGN_IN_ERRORS } from "../lib/router.js";
import { getPasskey, passkeyErrorMessage, passkeysSupported } from "../lib/webauthn.js";
import { Field } from "../components/Field.js";
import { Icon, type IconName } from "../components/Icon.js";
import { Logo } from "../components/Logo.js";
import { Notice } from "../components/Notice.js";
import { VcnetCredit } from "../components/VcnetCredit.js";

/**
 * The door, and the one screen a signed-out visitor (or a crawler checking an
 * OAuth app) ever sees. So it does two jobs: say what utucat is in the time
 * it takes to glance at it, and let an invited person in by whichever route
 * they have.
 *
 * The left half is the pitch: the tagline, a small search that types itself
 * and finds something (which is the product in one loop), and the three
 * things it does. The loop runs through the household examples, then turns
 * violet and runs utucat stock's -- with stock's three things underneath and
 * the "stock" tag on the wordmark -- then turns back: two products, one door. On a phone the pitch splits around the card: the lockup and
 * tagline above, the rest below, so the form is on the first screen.
 *
 * The honest answer nobody likes is still here: an address that is not on the
 * invite list is told so. See docs/notes/auth.md.
 */
export function SignIn({
  initialError,
  linkToken,
  onSignedIn,
}: {
  initialError: string | null;
  /** From a mailed link: the card asks for one press instead of offering the methods. */
  linkToken: string | null;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    initialError ? (SIGN_IN_ERRORS[initialError] ?? "That link did not work. Ask for a new one.") : null,
  );
  const passkeys = passkeysSupported();
  const [confirming, setConfirming] = useState(false);
  const [confirmable, setConfirmable] = useState(Boolean(linkToken));

  // Opening the link claims nothing (mail scanners open links); this press does.
  async function confirm() {
    if (!linkToken) return;
    setConfirming(true);
    setError(null);
    try {
      await api.confirmSignIn(linkToken);
      await onSignedIn();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
      setConfirmable(false);
    } finally {
      setConfirming(false);
    }
  }

  // The pitch's loop: every household example, then every utucat stock one
  // (the hero turns violet for those), then round again. With reduced motion
  // it holds still on the first household example.
  const still =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [demo, setDemo] = useState(0);
  const example = DEMO[demo % DEMO.length]!;
  const stock = example.stock === true;
  const next = useCallback(() => setDemo((current) => current + 1), []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      await api.signInLink(email.trim());
      setSent(true);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
    } finally {
      setSending(false);
    }
  }

  async function withPasskey() {
    setPasskeyBusy(true);
    setError(null);
    try {
      const { options } = await api.passkeys.loginOptions();
      const response = await getPasskey(options);
      await api.passkeys.loginVerify(response);
      await onSignedIn();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : passkeyErrorMessage(problem));
    } finally {
      setPasskeyBusy(false);
    }
  }

  return (
    <div className="auth">
      {/* The violet backdrop, faded in over the blue while the demo shows
          utucat stock. A layer of its own because a gradient cannot
          transition; opacity can. */}
      <div className="auth-backdrop-stock" data-on={stock || undefined} aria-hidden="true" />

      <section className="auth-brand" data-kind={stock ? "stock" : undefined}>
        <Logo size="lg" stock={stock} />
        <h1 className="auth-headline">
          Organize today.
          <br />
          Find tomorrow.
        </h1>
      </section>

      <section
        className="auth-story"
        data-kind={stock ? "stock" : undefined}
        aria-label={stock ? "What utucat stock does" : "What utucat does"}
      >
        <SearchDemo example={example} still={still} onDone={next} />
        {stock ? (
          <ul className="auth-features" key="stock">
            <Feature icon="clipboard" title="Receive it">
              Against the vendor's invoice, serials and all.
            </Feature>
            <Feature icon="external" title="Deploy it">
              To client sites, and back to the shelf.
            </Feature>
            <Feature icon="download" title="Bill it">
              Month-end CSV for QuickBooks, ConnectWise or Autotask.
            </Feature>
          </ul>
        ) : (
          <ul className="auth-features" key="home">
            <Feature icon="camera" title="Snap it">
              Photo first. Details whenever.
            </Feature>
            <Feature icon="search" title="Find it">
              Every word, shelf and note, in seconds.
            </Feature>
            <Feature icon="bag" title="Sell it">
              Marketplace and classifieds listings, ready to paste.
            </Feature>
          </ul>
        )}
      </section>

      <main className="auth-main">
        <div className="auth-card">
          {confirmable ? (
            <div className="auth-sent">
              <span className="auth-sent-icon">
                <Icon name="mail" size={26} />
              </span>
              <h2 className="auth-title">Finish signing in</h2>
              <p className="muted">Press the button to sign in on this device.</p>
              {error && <Notice tone="error">{error}</Notice>}
              <button
                className="button button-block"
                type="button"
                disabled={confirming}
                onClick={() => void confirm()}
              >
                {confirming ? "Signing in…" : "Sign in"}
              </button>
            </div>
          ) : sent ? (
            <div className="auth-sent">
              <span className="auth-sent-icon">
                <Icon name="mail" size={26} />
              </span>
              <h2 className="auth-title">Check your inbox</h2>
              <p className="muted">
                A sign-in link is on its way to <strong>{email.trim()}</strong>. It works once, for 15
                minutes.
              </p>
              <button
                className="button button-quiet button-block"
                type="button"
                onClick={() => setSent(false)}
              >
                Use another address
              </button>
            </div>
          ) : (
            <>
              <div className="auth-card-head">
                <h2 className="auth-title">Sign in</h2>
                <span className="pill">Invite only</span>
              </div>

              {error && <Notice tone="error">{error}</Notice>}

              <div className="stack">
                {passkeys && (
                  <button
                    className="button button-block"
                    type="button"
                    disabled={passkeyBusy}
                    onClick={() => void withPasskey()}
                  >
                    <Icon name="key" size={18} />
                    {passkeyBusy ? "Waiting for your device…" : "Sign in with a passkey"}
                  </button>
                )}
                <a className="button button-quiet button-block" href={ROUTES.googleStart}>
                  <img className="provider-logo" src="/brand/google.svg" alt="" width={18} height={18} />
                  Continue with Google
                </a>
                <a className="button button-quiet button-block" href={ROUTES.microsoftStart}>
                  <img className="provider-logo" src="/brand/microsoft.svg" alt="" width={18} height={18} />
                  Continue with Microsoft
                </a>
              </div>

              <div className="divider-text">
                <span>or</span>
              </div>

              <form className="stack" onSubmit={submit}>
                <Field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  autoComplete="email webauthn"
                  required
                />
                <button
                  className={passkeys ? "button button-quiet button-block" : "button button-block"}
                  type="submit"
                  disabled={sending || email.trim().length === 0}
                >
                  <Icon name="mail" size={18} />
                  {sending ? "Sending…" : "Email me a link"}
                </button>
              </form>

              <p className="auth-fine">
                We only ask for your email, or with Google or Microsoft, your name, email and profile photo.
                Nothing is ever posted for you.
              </p>
            </>
          )}
        </div>

        <p className="auth-legal">
          <a href="https://vcnet.us/privacy" target="_blank" rel="noopener noreferrer">
            Privacy
          </a>
          <span aria-hidden="true">·</span>
          <a href="https://vcnet.us/tos" target="_blank" rel="noopener noreferrer">
            Terms
          </a>
        </p>
        <VcnetCredit />
      </main>
    </div>
  );
}

function Feature({ icon, title, children }: { icon: IconName; title: string; children: string }) {
  return (
    <li className="auth-feature">
      <span className="auth-feature-icon">
        <Icon name={icon} size={20} />
      </span>
      <span>
        <strong>{title}</strong>
        <span>{children}</span>
      </span>
    </li>
  );
}

/* ------------------------------------------------------ the search demo -- */

interface Example {
  query: string;
  title: string;
  where: string;
  icon: IconName;
  badge?: string;
  /** Shown while the hero is utucat stock. */
  stock?: boolean;
}

const DEMO: Example[] = [
  { query: "drill", title: "Cordless drill", where: "Garage › Shelf B", icon: "zap", badge: "For sale" },
  { query: "passport", title: "Passport", where: "Office › Desk › Top drawer", icon: "bookmark" },
  { query: "tent", title: "4-person tent", where: "Basement › Bin 3", icon: "box" },
  {
    query: "LAP-4471",
    title: "ThinkPad T14 · LAP-4471",
    where: "Acme, Leeds office",
    icon: "monitor",
    badge: "Deployed",
    stock: true,
  },
  {
    query: "usb-c dock",
    title: "USB-C dock",
    where: "Stockroom · 17 on the shelf",
    icon: "box",
    stock: true,
  },
  {
    query: "INV-7731",
    title: "Ingram invoice · 24 lines",
    where: "Received 1 September",
    icon: "clipboard",
    badge: "Waiting to bill",
    stock: true,
  },
];

/**
 * A search box that types a word and finds the thing: the product, in one
 * loop. Decoration only (`aria-hidden`), since the features list beside it
 * says the same in words. Which example is showing is the screen's to decide
 * (the hero changes colour with it); this types it out and says when it has
 * been on screen long enough.
 */
function SearchDemo({ example, still, onDone }: { example: Example; still: boolean; onDone: () => void }) {
  const [typed, setTyped] = useState(0);
  const done = still || typed >= example.query.length;

  useEffect(() => {
    if (still) return;
    const timer = done
      ? window.setTimeout(() => {
          setTyped(0);
          onDone();
        }, 2600)
      : window.setTimeout(() => setTyped((current) => current + 1), typed === 0 ? 500 : 90);
    return () => window.clearTimeout(timer);
  }, [still, done, typed, onDone]);

  return (
    <div className="demo" aria-hidden="true">
      <div className="demo-search">
        <Icon name="search" size={18} />
        <span className="demo-typed">{still ? example.query : example.query.slice(0, typed)}</span>
        <span className="demo-caret" />
      </div>
      <div className={done ? "demo-result demo-result-in" : "demo-result"} key={example.query}>
        <span className="demo-thumb">
          <Icon name={example.icon} size={22} />
        </span>
        <span className="demo-text">
          <strong>{example.title}</strong>
          <span>
            <Icon name="pin" size={13} />
            {example.where}
          </span>
        </span>
        {example.badge && <span className="demo-badge">{example.badge}</span>}
      </div>
    </div>
  );
}
